import { useState } from "react";
import { ArrowLeft, Check, Tag, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useContent } from "@/context/ContentContext";

/**
 * Karta podsumowania zamówienia — pokazana między submit a wysłaniem.
 * Klient widzi wszystkie wpisane dane + „Potwierdź" / „Wróć do edycji".
 * Jeśli włączone: pole na kod rabatowy.
 */
export default function OrderSummary({ testIdPrefix, plan, form, consents, loading, onConfirm, onBack, extraRows = [] }) {
  const { brand } = useContent();
  const discountsEnabled = !!brand?.discount_codes_enabled;
  const originalPrice = plan?.price ? Number(plan.price) : null;

  const [code, setCode] = useState("");
  const [applied, setApplied] = useState(null); // {code, final_price, saved, ...}
  const [checking, setChecking] = useState(false);

  const applyCode = async () => {
    if (!code.trim() || !plan) return;
    setChecking(true);
    try {
      const { data } = await api.post("/discount-codes/validate", {
        code: code.trim(),
        plan_id: plan.id,
        plan_price: originalPrice,
      });
      setApplied(data);
      toast.success(`Rabat naliczony: -${data.saved} zł`);
    } catch (e) {
      setApplied(null);
      toast.error(e?.response?.data?.detail || "Kod nieprawidłowy");
    } finally {
      setChecking(false);
    }
  };

  const clearCode = () => { setCode(""); setApplied(null); };

  const finalPrice = applied?.final_price ?? originalPrice;
  const saved = applied?.saved ?? 0;

  const Row = ({ label, value, testid }) => (
    <div className="grid grid-cols-[140px,1fr] gap-3 py-2 border-b border-slate-100 last:border-0">
      <div className="text-xs font-bold text-slate-500 uppercase tracking-wider">{label}</div>
      <div className="text-sm text-slate-800 font-semibold break-words" data-testid={testid}>
        {value || <span className="text-slate-300">—</span>}
      </div>
    </div>
  );

  const address = [
    form.address_street,
    form.house_number ? `${form.house_number}${form.apartment_number ? "/" + form.apartment_number : ""}` : "",
  ].filter(Boolean).join(" ");
  const cityLine = [form.address_postal, form.address_city].filter(Boolean).join(" ");

  const doConfirm = () => {
    onConfirm(applied ? { discount_code: applied.code, discount_value: saved, final_price: finalPrice } : null);
  };

  return (
    <div data-testid={`${testIdPrefix}-summary`} className="rm-card p-8 md:p-10 max-w-3xl mx-auto">
      <h1 className="font-display text-3xl font-black text-slate-900 mb-1">Sprawdź swoje zamówienie</h1>
      <p className="text-slate-500 mb-6">Zanim wyślemy — upewnij się, że wszystko się zgadza.</p>

      {plan && (
        <div className="rounded-xl bg-[#1f3a8a]/5 border border-[#1f3a8a]/20 p-4 mb-6">
          <div className="text-xs font-bold text-[#1f3a8a] uppercase tracking-wider">Wybrany abonament</div>
          <div className="flex items-baseline gap-2 mt-1 flex-wrap">
            <span className="text-2xl font-black text-slate-900" data-testid={`${testIdPrefix}-summary-plan`}>{plan.name}</span>
            {applied ? (
              <>
                <span className="text-lg font-bold text-slate-400 line-through">{originalPrice} zł{plan.period}</span>
                <span className="text-lg font-bold text-[#dc2626]" data-testid={`${testIdPrefix}-summary-final-price`}>{finalPrice} zł{plan.period}</span>
                <span className="ml-auto text-xs font-bold text-green-700 bg-green-100 px-2 py-1 rounded-full">Oszczędzasz {saved} zł</span>
              </>
            ) : (
              <span className="text-lg font-bold text-[#dc2626]">{plan.price} zł{plan.period}</span>
            )}
          </div>
        </div>
      )}

      {discountsEnabled && plan && (
        <div className="rounded-xl bg-[#dc2626]/5 border border-[#dc2626]/20 p-4 mb-6">
          <div className="flex items-center gap-2 text-xs font-bold text-[#dc2626] uppercase tracking-wider mb-2">
            <Tag size={14} /> Masz kod rabatowy?
          </div>
          {applied ? (
            <div className="flex items-center gap-2">
              <span className="font-mono font-bold text-[#dc2626] bg-white px-3 py-1.5 rounded-lg border border-[#dc2626]/30" data-testid={`${testIdPrefix}-summary-applied-code`}>{applied.code}</span>
              <span className="text-sm text-slate-600">— zastosowany</span>
              <button type="button" data-testid={`${testIdPrefix}-summary-clear-code`} onClick={clearCode} className="ml-auto text-slate-400 hover:text-red-500" title="Usuń kod">
                <X size={16} />
              </button>
            </div>
          ) : (
            <div className="flex gap-2">
              <input
                data-testid={`${testIdPrefix}-summary-code-input`}
                className="rm-input flex-1 uppercase font-mono"
                placeholder="np. WIOSNA2026"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), applyCode())}
              />
              <button
                type="button"
                data-testid={`${testIdPrefix}-summary-apply-code`}
                onClick={applyCode}
                disabled={checking || !code.trim()}
                className="px-5 rounded-full bg-[#1f3a8a] hover:bg-[#15296b] text-white font-semibold text-sm disabled:opacity-50"
              >
                {checking ? "…" : "Sprawdź"}
              </button>
            </div>
          )}
        </div>
      )}

      <section className="mb-5">
        <h3 className="font-display font-bold text-slate-800 text-lg mb-2">Dane osobowe</h3>
        <div className="rounded-lg bg-white border border-slate-200 px-4">
          <Row testid={`${testIdPrefix}-summary-name`} label="Imię i nazwisko" value={`${form.first_name || ""} ${form.last_name || ""}`.trim()} />
          <Row testid={`${testIdPrefix}-summary-email`} label="Email" value={form.email} />
          <Row testid={`${testIdPrefix}-summary-phone`} label="Telefon" value={form.phone || form.current_number} />
          <Row testid={`${testIdPrefix}-summary-pesel`} label="PESEL" value={form.pesel} />
          {extraRows.map((r, i) => (
            <Row key={i} testid={`${testIdPrefix}-summary-extra-${i}`} label={r.label} value={r.value} />
          ))}
        </div>
      </section>

      <section className="mb-5">
        <h3 className="font-display font-bold text-slate-800 text-lg mb-2">Adres dostawy</h3>
        <div className="rounded-lg bg-white border border-slate-200 px-4">
          <Row testid={`${testIdPrefix}-summary-street`} label="Ulica i nr" value={address} />
          <Row testid={`${testIdPrefix}-summary-city`} label="Miasto" value={cityLine} />
        </div>
      </section>

      {form.notes && (
        <section className="mb-5">
          <h3 className="font-display font-bold text-slate-800 text-lg mb-2">Uwagi</h3>
          <div className="rounded-lg bg-white border border-slate-200 p-4 text-sm text-slate-700 whitespace-pre-line" data-testid={`${testIdPrefix}-summary-notes`}>{form.notes}</div>
        </section>
      )}

      <section className="mb-6">
        <h3 className="font-display font-bold text-slate-800 text-lg mb-2">Zaakceptowane zgody</h3>
        <ul className="text-sm text-slate-700 space-y-1">
          {consents.rodo && <li>✓ Zapoznałem/am się z klauzulą informacyjną RODO</li>}
          {consents.privacy && <li>✓ Polityka Prywatności</li>}
          {consents.orders && <li>✓ Przetwarzanie danych w celu realizacji zamówień</li>}
          {consents.marketing && <li>✓ Marketing</li>}
          {consents.electronic && <li>✓ Informacje handlowe elektroniczne</li>}
        </ul>
      </section>

      <div className="flex flex-col-reverse sm:flex-row gap-3 pt-4 border-t border-slate-200">
        <button
          type="button"
          data-testid={`${testIdPrefix}-summary-back`}
          onClick={onBack}
          disabled={loading}
          className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border-2 border-slate-300 text-slate-700 font-bold hover:bg-slate-100 transition disabled:opacity-60"
        >
          <ArrowLeft size={18} /> Wróć do edycji
        </button>
        <button
          type="button"
          data-testid={`${testIdPrefix}-summary-confirm`}
          onClick={doConfirm}
          disabled={loading}
          className="inline-flex items-center justify-center gap-2 flex-1 bg-[#dc2626] hover:bg-[#ef4444] text-white font-bold px-6 py-3 rounded-full shadow-lg shadow-[#dc2626]/30 transition disabled:opacity-60"
        >
          <Check size={18} /> {loading ? "Wysyłanie…" : "Potwierdź i wyślij zamówienie"}
        </button>
      </div>
    </div>
  );
}
